import React from 'react';
import { motion } from 'framer-motion';
import { Activity, BookOpen, Home as HomeIcon } from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.6, ease: "easeOut" } 
  },
};

export default function CorePillars() {
  const pillars = [
    { 
      icon: <HomeIcon size={32} />, 
      title: "Shelter & Security", 
      desc: "Providing safe housing for orphans and homeless children in a nurturing family environment." 
    },
    { 
      icon: <BookOpen size={32} />, 
      title: "Education & Skills", 
      desc: "Formal schooling and vocational training to ensure self-reliance and a professional future." 
    },
    { 
      icon: <Activity size={32} />, 
      title: "Health & Wellbeing", 
      desc: "Specialized medical care and therapy for children with disabilities and those needing physical recovery." 
    }
  ];

  return (
    <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
      {/* Subtle Background Decoration */}
      <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none">
        <div className="absolute top-10 left-10 w-64 h-64 bg-gh-orange rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-gh-blue rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-gh-orange font-bold tracking-widest text-sm mb-2 uppercase">How We Help</h2>
          <h3 className="text-4xl md:text-5xl font-black text-white leading-tight">Our Core Pillars</h3>
          <div className="w-20 h-1 bg-gh-orange mx-auto mt-4 rounded-full"></div>
        </motion.div>

        <motion.div 
          className="grid md:grid-cols-3 gap-10"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {pillars.map((item, idx) => (
            <motion.div 
              key={idx} 
              variants={itemVariants}
              className="group p-10 bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 hover:border-gh-orange/50 transition-all duration-500 hover:-translate-y-2"
            >
              <div className="mb-6 text-gh-orange group-hover:scale-110 transition-transform duration-300">
                {item.icon}
              </div>
              <h4 className="text-2xl font-bold mb-4 text-white group-hover:text-gh-orange transition-colors">
                {item.title}
              </h4>
              <p className="text-slate-400 leading-relaxed font-medium">
                {item.desc}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}